import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { MessageSendingInput } from "../../../../../../common/ChatComponents/MessageSendingInput";
import { SendMessageButton } from "../../../../../../common/ChatComponents/MessageSendingInput/Actions/SendMessageButton";
import { AppIconButton } from "../../../../../../shared/components/AppIconButton";
import { AppFileTag } from "../../../../../../shared/components/AppFileTag";
import { PaperclipIcon } from "../../../../../../shared/icons/PaperclipIcon";
import { UploadFileButton } from "../../../../../../common/Components/Buttons/UploadFileButton";
import { useAppSelector } from "../../../../../../services/redux/hooks/use-selector";
import { selectUserArtifactGroup } from "../../../../../Auth/redux/selectors";
import { AttachedFileMenu } from "../../../../../../common/Components/Menu/AttachedFileMenu";
import { useAppMenus } from "../../../../../../app/state/menuSlice/hook/use-app-menus";
import { AttachedFileModal } from "../../../../../Modals/AttachedFileModal";
import { UPLOADED_FILE_EXTENSIONS } from "../../../../../Modals/AttachedFileModal/constants";
import { useAppModals } from "../../../../../../app/state/modalSlice/hook/use-app-modals";
import { AppLoading } from "../../../../../../shared/components/AppLoading";
import { ClearButton } from "../../../../../../common/Components/Buttons/ClearButton";
import { AppButton } from "../../../../../../shared/components/AppButton";
import styles from "./styles.module.css";

interface Props {
  sendValue: string;
  fileName: string | null;
  isFileLoading: boolean;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  sendMessage: () => void;
  handleClick: (hash: string, fileName: string) => void;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUploadHtml: () => void;
  handleKeyDown: (event: any) => void;
  handleDeleteFileTag: () => void;
  clearChat: () => void;
  onPaste?: (e: React.ClipboardEvent) => void;
}

export const EditorMessageSanding = ({
  sendValue,
  fileName,
  isFileLoading,
  onPaste,
  onChange,
  handleClick,
  sendMessage,
  clearChat,
  handleUploadHtml,
  handleKeyDown,
  handleFileUpload,
  handleDeleteFileTag,
}: Props) => {
  const artifact = useAppSelector(selectUserArtifactGroup);
  const { handleCloseMenu, handleOpenMenu } = useAppMenus();
  const { handleCloseModal, handleOpenModal } = useAppModals();
  const [height, setHeight] = useState(100);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef({
    isActive: false,
    startY: 0,
    startHeight: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setHeight(100);
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      document.body.style.userSelect = "";
      document.body.style.pointerEvents = "";
    };
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current.isActive || !containerRef.current) return;

    const deltaY = e.clientY - isDragging.current.startY;
    const newHeight = Math.max(
      100,
      Math.min(500, isDragging.current.startHeight - deltaY),
    );

    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      setHeight(newHeight);
      containerRef.current.style.height = `${newHeight}px`;
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current.isActive) return;

    document.body.style.userSelect = "";
    document.body.style.pointerEvents = "";

    isDragging.current.isActive = false;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current.isActive = true;
      isDragging.current.startY = e.clientY;
      isDragging.current.startHeight = height;

      document.body.style.userSelect = "none";
      document.body.style.pointerEvents = "none";

      const controller = new AbortController();
      abortControllerRef.current = controller;

      document.addEventListener("mousemove", handleMouseMove, {
        signal: controller.signal,
      });
      document.addEventListener("mouseup", handleMouseUp, {
        signal: controller.signal,
        once: true,
      });

      e.preventDefault();
    },
    [height, handleMouseMove, handleMouseUp],
  );

  return (
    <>
      {fileName && (
        <div className={styles["file-tags-container"]}>
          <AppFileTag
            fileName={fileName}
            onDelete={handleDeleteFileTag}
            className={styles["editor-file-tag"]}
          />
          {isFileLoading && (
            <AppLoading settingSize={16} className={styles["loader"]} />
          )}
        </div>
      )}
      <div
        ref={containerRef}
        style={{
          height: `${height}px`,
        }}
        className={styles["resize-wrapper"]}
      >
        <div
          onMouseDown={handleMouseDown}
          className={styles["resize-control"]}
        />
        <MessageSendingInput
          sendValue={sendValue}
          onChange={onChange}
          handleKeyDown={handleKeyDown}
          inputStyle="elevated"
          className={styles["floating"]}
          onPaste={onPaste}
          actions={
            <>
              <ClearButton
                typeBtn={"icon_btn"}
                iconType={"broom"}
                btnVariant={"outlined"}
                className={styles["input-action"]}
                onClick={clearChat}
              />
              {artifact ? (
                <AppIconButton
                  onClick={(e) =>
                    handleOpenMenu(
                      e,
                      "attached_file_to_chat",
                      null,
                      "editor_chat",
                    )
                  }
                  buttonType={"outlined"}
                  className={styles["input-action"]}
                >
                  <PaperclipIcon />
                </AppIconButton>
              ) : (
                <UploadFileButton
                  icon={<PaperclipIcon />}
                  type={"icon_button"}
                  handleFileUpload={handleFileUpload}
                />
              )}
              <SendMessageButton sendMessage={sendMessage} />
            </>
          }
        />
      </div>
      <AttachedFileMenu
        subtype={"editor_chat"}
        closeMenu={handleCloseMenu}
        additionalOption={
          <AppButton
            onClick={handleUploadHtml}
            buttonType={"text"}
            className={styles["menu-btn"]}
          >
            Текущий html
          </AppButton>
        }
        handleUploadFile={(e) => {
          handleFileUpload(e);
          handleCloseMenu("attached_file_to_chat");
        }}
        openAttachedFileModal={() => {
          handleOpenModal("attached_file_modal", null);
          handleCloseMenu("attached_file_to_chat");
        }}
      />
      <AttachedFileModal
        extensions={UPLOADED_FILE_EXTENSIONS}
        onClose={handleCloseModal}
        handleClick={handleClick}
        multiple={false}
      />
    </>
  );
};
